import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiEdit,
  FiGrid,
  FiList,
  FiPlus,
  FiSettings,
  FiShield,
  FiTrash2,
  FiUsers,
} from "react-icons/fi"

import { UsersService } from "@/client"
import type { UserPublic } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import { UserOrganizationsList } from "@/components/Users/UserOrganizationsList"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/user-management")({
  component: UserManagement,
})

function UserManagement() {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const navigate = useNavigate()
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const [userToDelete, setUserToDelete] = useState<UserPublic | null>(null)
  const [viewMode, setViewModeState] = useState<"grid" | "list">(
    () =>
      (localStorage.getItem("user-management-view-mode") as "grid" | "list") ||
      "grid",
  )
  const setViewMode = (mode: "grid" | "list") => {
    setViewModeState(mode)
    localStorage.setItem("user-management-view-mode", mode)
  }
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)

  const {
    data: usersResponse,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: () => UsersService.readUsers({ skip: 0, limit: 100 }),
  })

  const users = usersResponse?.data || []

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => UsersService.deleteUser({ id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      showToast.showSuccessToast(t("admin:users.deleted"))
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail || "Failed to delete user"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const bulkDeleteMutation = useMutation({
    // Delete sequentially rather than firing every DELETE at once — a large
    // selection would otherwise grab a DB connection per request and pressure
    // the pool. Individual failures are counted, not fatal.
    mutationFn: async (ids: string[]) => {
      let failed = 0
      for (const id of ids) {
        try {
          await UsersService.deleteUser({ id })
        } catch {
          failed++
        }
      }
      return { total: ids.length, failed }
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      setSelectedIds(new Set())
      setConfirmBulkDelete(false)
      if (failed > 0) {
        showToast.showErrorToast(
          t("admin:users.bulkDeletePartial", {
            success: total - failed,
            failed,
          }),
        )
      } else {
        showToast.showSuccessToast(
          t("admin:users.bulkDeleted", { count: total }),
        )
      }
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const allSelected = users.length > 0 && selectedIds.size === users.length
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(users.map((u) => u.id)))
  }

  const handleEditPermissions = (user: UserPublic) => {
    navigate({ to: "/user-permissions/$userId", params: { userId: user.id } })
  }

  const handleDeleteUser = (user: UserPublic) => {
    setUserToDelete(user)
  }

  const getRoleBadge = (user: UserPublic) => {
    if (user.is_superuser) {
      return (
        <Badge colorScheme="purple" size="sm">
          {t("admin:users.superuser")}
        </Badge>
      )
    }
    // In a real implementation, you'd get the user's role from their permissions
    return (
      <Badge colorScheme="blue" size="sm">
        {t("admin:users.user")}
      </Badge>
    )
  }

  const getStatusBadge = (user: UserPublic) => {
    return user.is_active ? (
      <Badge colorScheme="green" size="sm">
        {t("common:status.active")}
      </Badge>
    ) : (
      <Badge colorScheme="gray" size="sm">
        {t("common:status.inactive")}
      </Badge>
    )
  }

  const ViewToggle = () => (
    <HStack bg={sc.toggleBg} borderRadius="10px" p="2px">
      <Box
        as="button"
        p={2}
        borderRadius="8px"
        bg={viewMode === "grid" ? sc.toggleActiveBg : "transparent"}
        color={viewMode === "grid" ? sc.heading : sc.textMuted}
        _hover={{ color: sc.heading }}
        transition="all 0.2s"
        onClick={() => setViewMode("grid")}
        title={t("common:actions.gridView")}
      >
        <FiGrid size={18} />
      </Box>
      <Box
        as="button"
        p={2}
        borderRadius="8px"
        bg={viewMode === "list" ? sc.toggleActiveBg : "transparent"}
        color={viewMode === "list" ? sc.heading : sc.textMuted}
        _hover={{ color: sc.heading }}
        transition="all 0.2s"
        onClick={() => setViewMode("list")}
        title={t("common:actions.tableView")}
      >
        <FiList size={18} />
      </Box>
    </HStack>
  )

  const emptyState = (
    <VisionCard variant="outline">
      <Flex direction="column" align="center" justify="center" py={8}>
        <IconBox variant="glass" size="lg" mb={4}>
          <FiUsers fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Heading size="md" color={sc.heading} mb={2}>
          {t("admin:users.noUsers")}
        </Heading>
        <Text color={sc.textMuted} textAlign="center" mb={4}>
          {t("admin:users.noUsersDesc")}
        </Text>
        <VisionButton
          variant="brand"
          onClick={() =>
            navigate({ to: "/user-form", search: { userId: undefined } })
          }
        >
          <FiPlus /> {t("admin:users.add")}
        </VisionButton>
      </Flex>
    </VisionCard>
  )

  const renderGridView = () => (
    <Grid
      templateColumns={{
        base: "1fr",
        md: "repeat(2, 1fr)",
        lg: "repeat(3, 1fr)",
      }}
      gap={6}
    >
      {users.map((user) => (
        <VisionCard key={user.id} variant="gradient">
          <Flex direction="column" h="100%">
            <Flex justify="space-between" align="flex-start" mb={4}>
              <Box flex="1">
                <Heading size="md" color={sc.heading} mb={2}>
                  {user.full_name || t("admin:users.noName")}
                </Heading>
                <Text color={sc.textSecondary} fontSize="sm" mb={2}>
                  {user.email}
                </Text>
                <HStack gap={2} mb={2}>
                  {getRoleBadge(user)}
                  {getStatusBadge(user)}
                </HStack>
                <UserOrganizationsList user={user} />
              </Box>
              <IconBox variant="glass" size="md">
                <FiUsers fontSize="20px" color={sc.iconColor} />
              </IconBox>
            </Flex>

            <Flex gap={2} mt="auto">
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({ to: "/user-form", search: { userId: user.id } })
                }
                flex="1"
              >
                <FiEdit /> {t("common:actions.edit")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() => handleEditPermissions(user)}
                flex="1"
              >
                <FiSettings /> {t("admin:users.permissions")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    to: "/admin-audit",
                    search: { user_id: user.id, page: 1 },
                  })
                }
                title={t("admin:users.audit", "View this user's audit log")}
              >
                <FiShield />
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                colorPalette="red"
                onClick={() => handleDeleteUser(user)}
              >
                <FiTrash2 />
              </VisionButton>
            </Flex>
          </Flex>
        </VisionCard>
      ))}

      {users.length === 0 && emptyState}
    </Grid>
  )

  const renderListView = () => (
    <VisionCard p={{ base: 3, md: "22px" }}>
      {users.length === 0 ? (
        emptyState
      ) : (
        <Box overflowX="auto" maxW="100%">
          <Table.Root size={{ base: "sm", md: "md" }}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader style={{ width: 40, minWidth: 40 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all users"
                    style={{
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                      accentColor: "#34a1dd",
                    }}
                  />
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("admin:users.columns.name")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("admin:users.columns.email")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("admin:users.columns.role")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor}>
                  {t("admin:users.columns.status")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.tableHeaderColor} textAlign="end">
                  {t("admin:users.columns.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((user) => {
                const isSelected = selectedIds.has(user.id)
                return (
                  <Table.Row
                    key={user.id}
                    bg={isSelected ? sc.toggleActiveBg : undefined}
                  >
                    <Table.Cell>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(user.id)}
                        aria-label={`Select ${user.email}`}
                        style={{
                          width: 16,
                          height: 16,
                          cursor: "pointer",
                          accentColor: "#34a1dd",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontWeight="500">
                      {user.full_name || t("admin:users.noName")}
                    </Table.Cell>
                    <Table.Cell color={sc.textSecondary} fontSize="sm">
                      {user.email}
                    </Table.Cell>
                    <Table.Cell>{getRoleBadge(user)}</Table.Cell>
                    <Table.Cell>{getStatusBadge(user)}</Table.Cell>
                    <Table.Cell>
                      <HStack gap={1} justify="flex-end">
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate({
                              to: "/user-form",
                              search: { userId: user.id },
                            })
                          }
                          title={t("common:actions.edit")}
                        >
                          <FiEdit />
                        </VisionButton>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPermissions(user)}
                          title={t("admin:users.permissions")}
                        >
                          <FiSettings />
                        </VisionButton>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate({
                              to: "/admin-audit",
                              search: { user_id: user.id, page: 1 },
                            })
                          }
                          title={t("admin:users.audit")}
                        >
                          <FiShield />
                        </VisionButton>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          colorPalette="red"
                          onClick={() => handleDeleteUser(user)}
                          title={t("common:actions.delete")}
                        >
                          <FiTrash2 />
                        </VisionButton>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VisionCard>
  )

  if (usersLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (usersError) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("admin:users.errorLoading")}
        </Heading>
        <Text color={sc.textMuted}>{t("admin:users.errorLoadingDesc")}</Text>
      </VisionCard>
    )
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={8} gap={4} wrap="wrap">
        <Box>
          <Heading size="xl" color={sc.heading} mb={2}>
            {t("admin:users.title")}
          </Heading>
          <Text color={sc.textMuted}>{t("admin:users.subtitle")}</Text>
        </Box>
        <HStack gap={3}>
          <ViewToggle />
          <VisionButton
            variant="brand"
            onClick={() =>
              navigate({ to: "/user-form", search: { userId: undefined } })
            }
          >
            <FiPlus />
            {t("admin:users.add")}
          </VisionButton>
        </HStack>
      </Flex>

      {selectedIds.size > 0 && (
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          p={3}
          borderRadius="12px"
          bg="rgba(255,100,100,0.08)"
          border="1px solid rgba(255,100,100,0.25)"
        >
          <Text fontSize="sm" color="red.300">
            {t("admin:users.selected", { count: selectedIds.size })}
          </Text>
          <HStack gap={2}>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              {t("common:actions.cancel")}
            </VisionButton>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => setConfirmBulkDelete(true)}
              style={{
                borderColor: "rgba(255,100,100,0.4)",
                color: "#FC8181",
              }}
            >
              <FiTrash2 /> {t("common:actions.deleteSelected")}
            </VisionButton>
          </HStack>
        </Flex>
      )}

      <Box position="relative">
        {bulkDeleteMutation.isPending && (
          <Flex
            position="absolute"
            inset={0}
            zIndex={10}
            align="center"
            justify="center"
            bg="rgba(0,0,0,0.45)"
            borderRadius="12px"
            gap={3}
          >
            <Spinner size="md" color={sc.spinnerColor} />
            <Text fontSize="sm" color="white">
              {t("common:actions.deleting", "Deleting…")}
            </Text>
          </Flex>
        )}
        {viewMode === "grid" ? renderGridView() : renderListView()}
      </Box>

      <ConfirmDialog
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={() => {
          if (userToDelete) deleteUserMutation.mutate(userToDelete.id)
        }}
        title={t("common:confirm.deleteTitle", { type: "User" })}
        message={t("admin:users.confirmDelete", {
          name: userToDelete?.full_name || userToDelete?.email,
        })}
        loading={deleteUserMutation.isPending}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={() => bulkDeleteMutation.mutate([...selectedIds])}
        title={t("admin:users.bulkDeleteTitle")}
        message={t("admin:users.confirmBulkDelete", {
          count: selectedIds.size,
        })}
        loading={bulkDeleteMutation.isPending}
      />
    </Box>
  )
}
